"use client";
import React, { useEffect, useRef, useState } from "react";
import icon from "@/components/icons/Icons";
import { AnimatePresence, motion } from "framer-motion";
import { useLoginModal } from "@/utils/useLoginModal";
import { Scheme } from "@/type/schemeType";
import { usePathname, useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import { increaseCart } from "@/redux/features/AddToCart/cartSlice";
import { apiPost } from "@/utils/apiClient";
import { addToast } from "@/components/ui/CustomToastManager";
import { DASHBOARD_ROOT } from "@/utils/gloableVariables";

type FundData = {
  schemeData: Scheme;
};

type APIResponse = {
  status: boolean;
  message: string;
  cart_id: number;
};

export default function InvestmentFormStrip({ schemeData }: FundData) {
  const getInitialType = () => {
    if (schemeData?.sip_allowed) return "SIP";
    if (schemeData?.lump_allowed) return "Lumpsum";
    return "SIP";
  };

  const getDefaultAmount = (type: "SIP" | "Lumpsum") => {
    if (type === "SIP") return schemeData?.minimum_sip_invest?.toLocaleString("en-IN") || "100";
    return schemeData?.minimum_investment?.toLocaleString("en-IN") || "100";
  };

  const dispatch = useDispatch();
  const [investmentType, setInvestmentType] = useState<"SIP" | "Lumpsum">(getInitialType());
  const [amount, setAmount] = useState(getDefaultAmount(getInitialType()));
  const [showError, setShowError] = useState<{ [key: string]: string }>({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const [cartAdd, setCartAdd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const pathName = usePathname();
  const route = useRouter();

  const MAX_INVESTMENT_AMOUNT = investmentType === "SIP" ? 1000000 : 10000000;

  const { open, isLoggedIn, setModalKey } = useLoginModal();

  const validateAmount = (raw: string | number) => {
    const num = typeof raw === "string" ? Number(raw.replace(/[^0-9]/g, "") || "0") : raw;
    const formatted = num.toLocaleString("en-IN");
    setAmount(formatted);

    const minAmount =
      investmentType === "SIP" ? schemeData?.minimum_sip_invest || 100 : schemeData?.minimum_investment || 500;

    if (num < minAmount) {
      setShowError({ amount: `Minimum investment amount is ₹${minAmount.toLocaleString()}.` });
    } else if (num % 100 !== 0) {
      setShowError({ amount: "Amount should be divisible by ₹100." });
    } else if (num > MAX_INVESTMENT_AMOUNT) {
      setShowError({ amount: `Maximum investment amount is under ₹${MAX_INVESTMENT_AMOUNT}` });
    } else {
      setShowError({});
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    validateAmount(raw);
  };

  const addAmount = (e: React.MouseEvent<HTMLButtonElement>, val: number) => {
    e.preventDefault();
    const current = Number(amount.replace(/[^0-9]/g, "") || "0");
    validateAmount(current + val);
  };

  const handleSelect = (day: number) => {
    setSelectedDay(day);
    setIsOpen(false);
  };

  const getOrdinalSuffix = (n: number) => {
    const j = n % 10,
      k = n % 100;
    if (j === 1 && k !== 11) return `${n}st`;
    if (j === 2 && k !== 12) return `${n}nd`;
    if (j === 3 && k !== 13) return `${n}rd`;
    return `${n}th`;
  };

  const createPayload = (): Record<string, string> => {
    const selectedAmount = parseInt(amount.replace(/[^0-9]/g, ""), 10);
    return {
      scheme_code: schemeData.scheme_code || "",
      inv_type: investmentType.toLowerCase(),
      amount: selectedAmount.toString(),
      sip_date: selectedDay.toString() || "1",
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: pathName,
      medium: "Web",
      folio: "",
    };
  };

  const handleCartAction = async ({ redirectAfter = false }: { redirectAfter?: boolean }) => {
    if (!isLoggedIn) return setIsLoading(false);

    const selectedAmount = parseInt(amount.replace(/[^0-9]/g, ""), 10);

    validateAmount(selectedAmount);

    if (Object.keys(showError).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const res: APIResponse = await apiPost("addToCart", createPayload());

      if (res.status) {
        if (redirectAfter) {
          route.push(`${DASHBOARD_ROOT}single-buy/${res.cart_id}`);
        } else {
          dispatch(increaseCart());
          setCartAdd(true);
          addToast({
            title: "Add To Cart",
            description: "Item added to cart!",
            status: "success",
          });
        }
      } else {
        addToast({
          title: "Add To Cart",
          description: "Failed to add to cart!",
          status: "failed",
        });
      }
    } catch (err) {
      console.error(err);
      setShowError({ button: `${err || "Error while adding to cart."}` });
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = () => {
    handleCartAction({ redirectAfter: false });
  };

  const handleStartSIPButton = () => handleCartAction({ redirectAfter: true });

  setTimeout(() => {
    if (showError.button) {
      setShowError({});
    }
  }, 3000);

  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true); // true => captures scrolls in bubbling and shadow roots
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const enableFund = schemeData?.sip_allowed || schemeData?.lump_allowed;

  const thumbUp = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="w-4 h-4"
    >
      <path
        d="M20 6L9 17l-5-5"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div
      className={`w-full max-w-91 mx-auto bg-[#F5F9FF] border border-[#E9E9EB] rounded-4xl p-5 h-105 sm:h-auto xl:h-105 flex flex-col ${enableFund ? null : "pointer-events-none select-none"}`}
      aria-disabled={!enableFund}
    >
      <div className="flex justify-center mb-5">
        {["SIP", "Lumpsum"].map((type) => {
          const isAllowed = type === "SIP" ? schemeData?.sip_allowed : schemeData?.lump_allowed;

          return (
            <label
              key={type}
              className={`group w-full max-w-150 ${!enableFund ? "border-none" : "border-b-2"} py-2 ${investmentType === type ? "border-primaryBlue" : "border-transparent hover:bg-[#f4f8ff]"} flex-1 transition-all duration-350 ease-in-out ${!isAllowed ? "opacity-50 cursor-not-allowed hover:bg-none" : "cursor-pointer"}`}
            >
              <input
                type="radio"
                name="investmentType"
                checked={investmentType === type}
                disabled={!isAllowed}
                onChange={() => {
                  if (isAllowed) {
                    setInvestmentType(type as "SIP" | "Lumpsum");
                    setShowError({});
                    setAmount(getDefaultAmount(type as "SIP" | "Lumpsum"));
                  }
                }}
                className="hidden"
              />
              <p
                className={`text-sm text-center ${investmentType === type ? "font-bold text-primaryBlack" : !isAllowed ? "text-[#444444]" : "text-[#444444] group-hover:text-primaryBlack group-hover:font-medium"}`}
              >
                {type === "SIP" ? "Monthly SIP" : "One Time"}
              </p>
            </label>
          );
        })}
      </div>

      <div className={`mb-4  ${enableFund ? null : "opacity-50"}`}>
        <p className="text-base mb-1.5 text-primaryBlack">{investmentType === "SIP" ? "SIP" : "Lumpsum"} Amount</p>

        <label className="block relative">
          <span
            className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${showError.amount ? "text-[#ec4d4d] " : "text-black "}`}
          >
            ₹
          </span>
          <input
            type="text"
            value={amount}
            onChange={handleAmountChange}
            className={`border text-sm font-normal w-full py-2.5 px-6 rounded-xl bg-white transition-all duration-200 ease-in-out ${showError.amount ? "border-[#ec4d4d] text-[#ec4d4d] " : "border-[#e4e4e4] hover:border-[#b6b5b5] text-black "}`}
          />
        </label>
        {showError.amount && <p className="text-2xs text-[#ec4d4d]">{showError.amount}</p>}
        <div className="flex gap-2 mt-3">
          {[1000, 3000, 5000].map((val) => (
            <button
              key={val}
              onClick={(e) => addAmount(e, val)}
              className="border border-[#e4e4e4] text-base font-normal text-[#a1a1a1] px-3 py-0.5 rounded-xl cursor-pointer hover:border-[#a7a7a7] hover:bg-[#f4f8ff] hover:text-[#666666] transition-all duration-300"
            >
              + ₹{val.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {investmentType === "SIP" && (
        <div className={`mb-4 ${enableFund ? null : "opacity-50"}`} ref={calendarRef}>
          <p className="text-base mb-1.5 text-primaryBlack">Monthly SIP Date</p>

          <div className="relative w-full">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
              <icon.calendarIcon className="text-primaryBlue" />
            </span>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`w-full py-2.5 px-10 rounded-xl text-left bg-white border text-sm font-normal text-black transition-all duration-200 ${isOpen ? "border-[#b6b5b5]" : "border-[#e4e4e4]"}`}
            >
              {selectedDay ? getOrdinalSuffix(selectedDay) : "Select Date"}
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 w-full mt-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-2 overflow-hidden"
                >
                  <div className="grid grid-cols-7 gap-1 text-sm text-center">
                    {Array.from({ length: 28 }, (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handleSelect(i + 1)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 ${selectedDay === i + 1 ? "border bg-[#f0f5fd] border-primaryBlue text-primaryBlue" : "hover:bg-[#f0f5fd]"}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {enableFund ? (
        <div className=" mt-auto">
          <div className="flex gap-3">
            <button
              onClick={
                isLoggedIn
                  ? handleAddToCart
                  : () => {
                      open();
                      setModalKey("investment-form-strip");
                    }
              }
              disabled={cartAdd || Object.keys(showError).length > 0}
              title={!cartAdd ? "Add This Fund In Cart" : "Fund Added To The Cart"}
              className={`w-full capitalize border border-[#1A51AA99] py-3 text-center rounded-xl flex-1 cursor-pointer transition-all duration-350 ease-in-out text-primaryBlue bg-transparent ${cartAdd || Object.keys(showError).length > 0 ? "flex items-center justify-center gap-1 opacity-80" : "hover:bg-white"}`}
            >
              {cartAdd && (
                <span className="w-6 h-6 inline-flex items-center justify-center overflow-hidden rounded-full bg-green-600">
                  {thumbUp}
                </span>
              )}
              {cartAdd ? "Fund Added" : isLoading ? "Loading ..." : "Add to Cart"}
            </button>

            <button
              onClick={
                isLoggedIn
                  ? handleStartSIPButton
                  : () => {
                      open();
                      setModalKey("investment-form-strip");
                    }
              }
              disabled={Object.keys(showError).length > 0}
              title="Click To Start SIP"
              className={`w-full capitalize border border-[#1A51AA99] py-3 text-center rounded-xl flex-1 cursor-pointer transition-all duration-350 ease-in-out text-white bg-primaryBlue  ${Object.keys(showError).length > 0 ? "opacity-50 pointer-events-none" : "shine-button-hover"}`}
            >
              {investmentType === "SIP" ? "Start SIP" : "Invest Now"}
            </button>
          </div>
          {showError.button && <p className="text-2xs text-[#ec4d4d]">{showError.button}</p>}
        </div>
      ) : (
        <div className="bg-[#EC4D4D] rounded-xl p-4 flex items-center gap-1.5 mt-auto">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM11.25 8C11.25 7.59 11.59 7.25 12 7.25C12.41 7.25 12.75 7.59 12.75 8V13C12.75 13.41 12.41 13.75 12 13.75C11.59 13.75 11.25 13.41 11.25 13V8ZM12.92 16.38C12.87 16.51 12.8 16.61 12.71 16.71C12.61 16.8 12.5 16.87 12.38 16.92C12.26 16.97 12.13 17 12 17C11.87 17 11.74 16.97 11.62 16.92C11.5 16.87 11.39 16.8 11.29 16.71C11.2 16.61 11.13 16.51 11.08 16.38C11.03 16.26 11 16.13 11 16C11 15.87 11.03 15.74 11.08 15.62C11.13 15.5 11.2 15.39 11.29 15.29C11.39 15.2 11.5 15.13 11.62 15.08C11.86 14.98 12.14 14.98 12.38 15.08C12.5 15.13 12.61 15.2 12.71 15.29C12.8 15.39 12.87 15.5 12.92 15.62C12.97 15.74 13 15.87 13 16C13 16.13 12.97 16.26 12.92 16.38Z"
              fill="white"
            />
          </svg>
          <p className="font-semibold text-sm leading-4 text-white">
            Fund Suspended: Currently this fund is not available for Investments.
          </p>
        </div>
      )}
    </div>
  );
}
